/**
 * "Recently Completed" feed (NAU-247) — real completed checklists with actor
 * initials. (A true cross-domain feed needs the backend; this is the
 * checklist slice we can build from existing data.)
 */

import { Avatar } from "@package/subframe/components/Avatar";
import { LinkButton } from "@package/subframe/components/LinkButton";
import { FeatherArrowRight } from "@subframe/core";
import { CheckCircle2 } from "lucide-react";

import type { RecentlyCompletedItem } from "@/lib/dashboard/dashboard-overview";

import { OverviewCard, SectionHeader } from "./_shared";
import { NAUD } from "./NaudIcon";

const AVATAR_VARIANTS = [
  "brand",
  "rose",
  "beige",
  "neutral",
  "brand-alt",
] as const;

interface RecentlyCompletedProps {
  items: RecentlyCompletedItem[];
  onViewAll: () => void;
}

export function RecentlyCompleted({
  items,
  onViewAll,
}: RecentlyCompletedProps) {
  return (
    <OverviewCard className="w-full p-5">
      <SectionHeader
        title="Recently completed"
        action={
          <LinkButton
            variant="brand"
            size="small"
            iconRight={<FeatherArrowRight />}
            onClick={onViewAll}
          >
            View all
          </LinkButton>
        }
      />
      {items.length === 0 ? (
        <div className="flex items-center justify-center py-8">
          <span className="text-body font-body text-subtext-color">
            Nothing completed yet today.
          </span>
        </div>
      ) : (
        <div className="mt-4 flex flex-col gap-3">
          {items.map((item, index) => (
            <div key={item.id} className="flex items-center gap-3">
              <CheckCircle2
                size={18}
                color={NAUD.green.solid}
                className="shrink-0"
              />
              <span className="grow truncate text-body font-body text-neutral-800">
                {item.title}
              </span>
              <span className="flex-none text-caption font-caption text-subtext-color">
                {item.timeLabel}
              </span>
              <Avatar
                size="small"
                variant={AVATAR_VARIANTS[index % AVATAR_VARIANTS.length]}
                title={item.actorName}
                aria-label={item.actorName}
              >
                {item.actorInitials}
              </Avatar>
            </div>
          ))}
        </div>
      )}
    </OverviewCard>
  );
}
